import { useEffect, useState } from "react";
import { Upload, LayoutGrid, FolderOpen, Loader2, Sparkles, Lock, Archive, RotateCcw } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  listPublishedSamples, listMySavedProjects, archiveStudioProject, unarchiveStudioProject, SAMPLE_CATEGORY_LABELS,
  type PaintSample, type SavedStudioProject,
} from "@/lib/studio/samples";

type Choice = "upload" | "sample" | "mine";

export function StartScreen({
  isSignedIn,
  onChooseUpload,
  onChooseSample,
  onOpenSaved,
}: {
  isSignedIn: boolean | null;
  onChooseUpload: () => void;
  onChooseSample: (sample: PaintSample) => void;
  onOpenSaved: (project: SavedStudioProject) => void;
}) {
  const [choice, setChoice] = useState<Choice | null>(null);
  const [samples, setSamples] = useState<PaintSample[] | null>(null);
  const [mine, setMine] = useState<SavedStudioProject[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (choice === "sample" && samples === null) {
      listPublishedSamples().then(setSamples).catch((e) => setLoadErr((e as Error).message));
    }
    if (choice === "mine" && isSignedIn) {
      setMine(null);
      listMySavedProjects(showArchived).then(setMine).catch((e) => setLoadErr((e as Error).message));
    }
  }, [choice, samples, isSignedIn, showArchived]);

  async function toggleArchive(p: SavedStudioProject, e: React.MouseEvent) {
    e.stopPropagation();
    setBusyId(p.id);
    try {
      if (showArchived) await unarchiveStudioProject(p.id);
      else await archiveStudioProject(p.id);
      const fresh = await listMySavedProjects(showArchived);
      setMine(fresh);
    } finally { setBusyId(null); }
  }

  if (choice === null) {
    return (
      <div className="max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          <StartCard
            icon={<Upload className="size-6" />}
            title="Upload Your Home"
            desc="Interior, exterior, cabinets, doors, deck or fence — any photo works."
            accent="amber"
            onClick={() => { setChoice("upload"); onChooseUpload(); }}
          />
          <StartCard
            icon={<LayoutGrid className="size-6" />}
            title="Try a Sample Project"
            desc="Start with a pre-masked room or exterior and jump straight to colors."
            accent="neutral"
            onClick={() => setChoice("sample")}
          />
          <StartCard
            icon={<FolderOpen className="size-6" />}
            title="Open My Projects"
            desc={isSignedIn ? "Reopen a project you saved to continue editing." : "Sign in to reopen saved projects."}
            accent="neutral"
            onClick={() => setChoice("mine")}
            locked={isSignedIn === false}
          />
        </div>
      </div>
    );
  }

  if (choice === "sample") {
    const filtered = (samples ?? []).filter((s) => category === "all" || s.category === category);
    const cats = Array.from(new Set((samples ?? []).map((s) => s.category)));
    return (
      <PickerShell title="Try a Sample Project" onBack={() => setChoice(null)}>
        {samples === null && !loadErr && (
          <div className="py-16 flex justify-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
        )}
        {loadErr && <ErrorLine msg={loadErr} />}
        {samples && samples.length === 0 && (
          <p className="text-zinc-500 text-sm text-center py-16">
            No sample projects published yet. <span className="text-zinc-400">Upload your own photo to get started.</span>
          </p>
        )}
        {samples && samples.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5 mb-5">
              <FilterChip active={category === "all"} onClick={() => setCategory("all")} label="All" />
              {cats.map((c) => (
                <FilterChip
                  key={c}
                  active={category === c}
                  onClick={() => setCategory(c)}
                  label={SAMPLE_CATEGORY_LABELS[c as keyof typeof SAMPLE_CATEGORY_LABELS] || c}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onChooseSample(s)}
                  className="group relative overflow-hidden rounded-xl border border-white/5 bg-white/[0.02] hover:border-amber-300/30 hover:bg-white/[0.04] transition text-left"
                >
                  <div className="aspect-[4/3] bg-neutral-900 relative overflow-hidden">
                    <img
                      src={s.thumbnail_url || s.source_image_url}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                    {s.is_featured && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-amber-300/90 text-zinc-950 px-1.5 py-0.5 rounded font-semibold">
                        <Sparkles className="size-2.5" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">
                      {SAMPLE_CATEGORY_LABELS[s.category] || s.category}
                    </p>
                    <h4 className="text-sm text-white font-medium">{s.title}</h4>
                    {s.description && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{s.description}</p>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </PickerShell>
    );
  }

  if (choice === "mine") {
    if (isSignedIn === false) {
      return (
        <PickerShell title="Open My Projects" onBack={() => setChoice(null)}>
          <div className="text-center py-16">
            <Lock className="size-8 text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-300 mb-6">Sign in to reopen projects you've saved.</p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 bg-amber-300 text-zinc-950 px-5 py-2.5 rounded-full text-sm font-semibold"
            >Sign in or create account</Link>
          </div>
        </PickerShell>
      );
    }
    return (
      <PickerShell title="Open My Projects" onBack={() => setChoice(null)}>
        <div className="flex items-center gap-1.5 mb-5">
          <FilterChip active={!showArchived} onClick={() => setShowArchived(false)} label="Active" />
          <FilterChip active={showArchived} onClick={() => setShowArchived(true)} label="Archived" />
        </div>
        {mine === null && !loadErr && (
          <div className="py-16 flex justify-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
        )}
        {loadErr && <ErrorLine msg={loadErr} />}
        {mine && mine.length === 0 && (
          <p className="text-zinc-500 text-sm text-center py-16">
            {showArchived ? "No archived projects." : "You haven't saved any projects yet. Upload a photo to create your first one."}
          </p>
        )}
        {mine && mine.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.map((p) => (
              <div
                key={p.id}
                className="group relative overflow-hidden rounded-xl border border-white/5 bg-white/[0.02] hover:border-amber-300/30 transition text-left"
              >
                <button onClick={() => onOpenSaved(p)} className="block w-full text-left">
                <div className="aspect-[4/3] bg-neutral-900 overflow-hidden">
                  <img
                    src={p.latest_render_url || p.source_image_url}
                    alt={p.name || "Project"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <h4 className="text-sm text-white font-medium truncate">{p.name || "Untitled project"}</h4>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Updated {new Date(p.updated_at).toLocaleDateString()}
                  </p>
                </div>
                </button>
                <button
                  onClick={(e) => toggleArchive(p, e)}
                  disabled={busyId === p.id}
                  title={showArchived ? "Restore" : "Archive"}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] bg-black/60 backdrop-blur border border-white/10 text-zinc-200 px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition hover:bg-black/80 disabled:opacity-50"
                >
                  {busyId === p.id ? <Loader2 className="size-3 animate-spin" /> : showArchived ? <RotateCcw className="size-3" /> : <Archive className="size-3" />}
                  {showArchived ? "Restore" : "Archive"}
                </button>
              </div>
            ))}
          </div>
        )}
      </PickerShell>
    );
  }

  return null;
}

function StartCard({
  icon, title, desc, accent, onClick, locked,
}: {
  icon: React.ReactNode; title: string; desc: string;
  accent: "amber" | "neutral"; onClick: () => void; locked?: boolean;
}) {
  const ring = accent === "amber"
    ? "border-amber-300/25 hover:border-amber-300/50 bg-gradient-to-br from-amber-300/[0.04] to-transparent"
    : "border-white/10 hover:border-white/25 bg-white/[0.02]";
  const iconBg = accent === "amber"
    ? "bg-amber-300/10 border-amber-300/20 text-amber-300"
    : "bg-white/5 border-white/10 text-zinc-300";
  return (
    <button
      onClick={onClick}
      className={`group relative rounded-2xl border p-6 sm:p-7 text-left transition ${ring} focus:outline-none focus:ring-2 focus:ring-amber-300/40`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border mb-4 transition group-hover:scale-105 ${iconBg}`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-white mb-1.5">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
      {locked && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-500">
          <Lock className="size-3" /> Sign in
        </span>
      )}
    </button>
  );
}

function PickerShell({ title, onBack, children }: { title: string; onBack: () => void; children: React.ReactNode }) {
  return (
    <div className="max-w-5xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-serif text-2xl text-white">{title}</h3>
        <button onClick={onBack} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white transition">
          ← Back
        </button>
      </div>
      {children}
    </div>
  );
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-medium transition ${
        active ? "bg-amber-300 text-zinc-950" : "bg-white/5 text-zinc-400 hover:text-white border border-white/5"
      }`}
    >{label}</button>
  );
}

function ErrorLine({ msg }: { msg: string }) {
  return (
    <p className="text-center py-8 text-red-400/80 text-sm">Couldn't load: {msg}</p>
  );
}